import { useRef } from 'react'
import { cn } from '@/lib/cn'
import { ChevronRightIcon } from 'lucide-react'
import { compararPorHora, type PublicacionResumen } from '@/lib/schemas/publicaciones'
import {
  INICIALES_SEMANA,
  diasDelMes,
  formatearDiaAgenda,
  hoyEnBuenosAires,
  semanaDe,
} from '@/features/grilla/formato'
import { PiezaCalendario } from './PiezaCalendario'

interface AgendaCelularProps {
  mes: string
  publicaciones: PublicacionResumen[]
  alAbrir: (id: string) => void
  esRastro: boolean
}

const COLOR_PUNTO: Record<PublicacionResumen['estado'], string> = {
  borrador: 'bg-borrador-tinta',
  para_revisar: 'bg-revisar-tinta',
  cambios: 'bg-cambios-tinta',
  aprobada: 'bg-aprobada-tinta',
}

/**
 * P02 celular (§8.3). Arriba, la tira de la semana con un punto por estado;
 * abajo, la agenda de los días que tienen piezas, agrupadas por día. Tocar un
 * día de la tira lleva la agenda hasta ahí.
 *
 * No hay arrastre: en una pantalla chica se reprograma desde Editar.
 */
export function AgendaCelular({
  mes,
  publicaciones,
  alAbrir,
  esRastro,
}: AgendaCelularProps) {
  const hoy = hoyEnBuenosAires()
  const contenedor = useRef<HTMLDivElement>(null)

  const porDia = new Map<string, PublicacionResumen[]>()
  for (const p of publicaciones) {
    const lista = porDia.get(p.fechaPautada)
    if (lista) lista.push(p)
    else porDia.set(p.fechaPautada, [p])
  }
  // Dentro del día, por hora; las que no tienen hora, al final (DEC-20).
  for (const lista of porDia.values()) lista.sort(compararPorHora)
  // La agenda lista sólo los días con piezas, del primero al último del mes.
  const conPiezas = [...porDia.keys()].sort()

  // La tira arranca en la semana de hoy si hoy cae en el mes; si no, en la
  // primera semana con piezas, para no abrir en un tramo vacío.
  const anclaje =
    hoy.slice(0, 7) === mes ? hoy : (conPiezas[0] ?? diasDelMes(mes)[7] ?? hoy)
  const semana = semanaDe(anclaje)

  const irADia = (dia: string) => {
    const destino = contenedor.current?.querySelector(`[data-agenda-dia="${dia}"]`)
    destino?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div data-calendario="agenda">
      <div className="grid grid-cols-7 gap-1 border-b border-borde pb-2">
        {semana.map((dia, i) => {
          const estados = new Set((porDia.get(dia) ?? []).map((p) => p.estado))
          return (
            <button
              key={dia}
              type="button"
              data-tira-dia={dia}
              onClick={() => {
                irADia(dia)
              }}
              className={cn(
                'flex flex-col items-center gap-0.5 rounded-lg py-1.5',
                dia === hoy && 'bg-hoy/10 ring-1 ring-hoy',
              )}
            >
              <span className="text-[11px] text-tinta-suave">{INICIALES_SEMANA[i]}</span>
              <span
                className={cn(
                  'text-[15px]',
                  dia.slice(0, 7) !== mes && 'text-tinta-suave',
                  dia === hoy && 'font-medium text-hoy',
                )}
              >
                {Number(dia.slice(8))}
              </span>
              <span className="flex h-1.5 items-center gap-0.5">
                {[...estados].slice(0, 3).map((e) => (
                  <span
                    key={e}
                    className={cn('size-1.5 rounded-full', COLOR_PUNTO[e])}
                    aria-hidden
                  />
                ))}
              </span>
            </button>
          )
        })}
      </div>

      <div ref={contenedor} className="flex flex-col">
        {conPiezas.map((dia) => (
          <section key={dia} data-agenda-dia={dia} className="scroll-mt-20 pt-4">
            <h2 className="px-1 pb-1 text-[15px] font-medium text-hoy">
              {formatearDiaAgenda(dia)}
              {dia === hoy && ' · Hoy'}
            </h2>
            <ul className="flex flex-col divide-y divide-borde">
              {(porDia.get(dia) ?? []).map((p) => (
                <li key={p.id} className="flex items-center">
                  <PiezaCalendario
                    publicacion={p}
                    alAbrir={alAbrir}
                    conTitulo
                    paraRastro={esRastro}
                    className="py-2"
                  />
                  <ChevronRightIcon
                    className="size-4 shrink-0 text-tinta-suave"
                    aria-hidden
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  )
}
